/**
 * Activity Log Utility
 * Logs user and admin actions to the activity-logs API
 */

const baseUrl = '/api/strapi';

/**
 * Every action type the CMS accepts. Must match the `actionType` enum in
 * cms/server/src/api/activity-log/content-types/activity-log/schema.json:
 * Strapi rejects a value missing from that enum with a 400, and logActivity()
 * only reports a failed write to the console, so the entry is lost without
 * anyone noticing. activityLog.test.ts reads the schema off disk and fails on
 * drift in either direction.
 */
export const ACTIVITY_ACTION_TYPES = [
  'create_post',
  'edit_post',
  'delete_post',
  'like_post',
  'comment_post',
  'create_event',
  'edit_event',
  'delete_event',
  'rsvp_event',
  'create_group',
  'edit_group',
  'delete_group',
  'join_group',
  'leave_group',
  'accept_member',
  'reject_member',
  'create_forum_topic',
  'reply_forum',
  'like_forum',
  'create_volunteer',
  'edit_volunteer',
  'delete_volunteer',
  'create_provider',
  'edit_provider',
  'delete_provider',
  'create_resource',
  'edit_resource',
  'create_team_bio',
  'edit_team_bio',
  'create_program_card',
  'edit_program_card',
  'edit_partners_section',
  'create_blog',
  'edit_blog',
  'delete_blog',
  'publish_blog',
  'like_blog',
  'comment_blog',
  'approve_user',
  'archive_user',
  'unarchive_user',
  'change_role',
  'flag_content',
  'unflag_content',
  'delete_flagged',
  'register',
  'login',
  'update_profile',
  'follow_user',
  'unfollow_user',
  'follow_group',
  'unfollow_group',
  'other',
] as const;

export type ActivityActionType = (typeof ACTIVITY_ACTION_TYPES)[number];

/**
 * Strapi creates `string` attributes as varchar(255). PostgreSQL (production)
 * rejects a longer value and SQLite (local dev) stores it, so an over-long
 * description loses the whole log entry in production only. logActivity()
 * clips actionDescription and targetName to this length before sending.
 */
export const MAX_LOG_FIELD_LENGTH = 255;

/**
 * Titles and group names are clipped to this length before they are combined
 * into one description, so even the longest combination fits inside
 * MAX_LOG_FIELD_LENGTH and the group name is never the part that gets cut.
 * activityLog.test.ts checks the worst case for every group-scoped helper.
 */
export const MAX_LOG_TITLE_LENGTH = 100;

/**
 * Shortens text to at most `max` characters, ending in an ellipsis when it was
 * cut. Counts code points (as PostgreSQL's varchar does), so an emoji's
 * surrogate pair is never split in half.
 */
export function clipLogText(text: string, max: number): string {
  const characters = Array.from(text);
  if (characters.length <= max) return text;
  return `${characters.slice(0, max - 1).join('')}…`;
}

/** The group an item was posted to, when it was posted to one. */
interface GroupContext {
  name: string;
  documentId?: string;
}

/** ` in group "Name"` for a group-scoped item, so a search for the group name finds it. */
function inGroup(group?: GroupContext): string {
  return group?.name ? ` in group "${clipLogText(group.name, MAX_LOG_TITLE_LENGTH)}"` : '';
}

/** The group's stable id, kept alongside the name in case the group is renamed later. */
function groupMetadata(group?: GroupContext): Record<string, unknown> | undefined {
  return group?.documentId ? { groupDocumentId: group.documentId } : undefined;
}

interface User {
  id: number | string;
  displayName?: string;
  username?: string;
  avatarUrl?: string;
  initials?: string;
  avatarColor?: string;
}

interface LogActivityParams {
  user: User;
  actionType: ActivityActionType;
  actionDescription: string;
  targetType?: string;
  targetId?: string;
  targetName?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Logs an activity to the activity-logs API
 * This is a fire-and-forget function - errors are logged but don't throw
 */
export async function logActivity({
  user: _user,
  actionType,
  actionDescription,
  targetType,
  targetId,
  targetName,
  metadata,
}: LogActivityParams): Promise<void> {
  try {
    const res = await fetch(`${baseUrl}/activity-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        data: {
          actionType,
          actionDescription: clipLogText(actionDescription, MAX_LOG_FIELD_LENGTH),
          targetType: targetType || null,
          targetId: targetId || null,
          targetName: targetName ? clipLogText(targetName, MAX_LOG_FIELD_LENGTH) : null,
          metadata: metadata || null,
        },
      }),
    });
    if (!res.ok) {
      console.error(`Failed to log activity: ${res.status} ${res.statusText}`);
    }
  } catch (error) {
    // Silent fail - activity logging should not break the app
    console.error('Failed to log activity:', error);
  }
}

// Helper functions for common activity types
export const ActivityLogger = {
  // Posts/News
  createPost: (user: User, postTitle: string, postId?: string, group?: GroupContext) =>
    logActivity({
      user,
      actionType: 'create_post',
      actionDescription: `Created new post "${clipLogText(postTitle, MAX_LOG_TITLE_LENGTH)}"${inGroup(group)}`,
      targetType: 'news-post',
      targetId: postId,
      targetName: postTitle,
      metadata: groupMetadata(group),
    }),

  // MWBI-608: likes are admin-activity-log only. Do not fan out to user-notification or email.
  likePost: (user: User, postTitle: string, postId?: string) =>
    logActivity({
      user,
      actionType: 'like_post',
      actionDescription: `Liked post "${postTitle}"`,
      targetType: 'news-post',
      targetId: postId,
      targetName: postTitle,
    }),

  commentPost: (user: User, postTitle: string, postId?: string) =>
    logActivity({
      user,
      actionType: 'comment_post',
      actionDescription: `Commented on post "${postTitle}"`,
      targetType: 'news-post',
      targetId: postId,
      targetName: postTitle,
    }),

  editPost: (user: User, postTitle: string, postId?: string) =>
    logActivity({
      user,
      actionType: 'edit_post',
      actionDescription: `Edited post "${postTitle}"`,
      targetType: 'news-post',
      targetId: postId,
      targetName: postTitle,
    }),

  deletePost: (user: User, postTitle: string, postId?: string) =>
    logActivity({
      user,
      actionType: 'delete_post',
      actionDescription: `Deleted post "${postTitle}"`,
      targetType: 'news-post',
      targetId: postId,
      targetName: postTitle,
    }),

  // Events
  createEvent: (user: User, eventTitle: string, eventId?: string) =>
    logActivity({
      user,
      actionType: 'create_event',
      actionDescription: `Created new event "${eventTitle}"`,
      targetType: 'event',
      targetId: eventId,
      targetName: eventTitle,
    }),

  rsvpEvent: (user: User, eventTitle: string, eventId?: string) =>
    logActivity({
      user,
      actionType: 'rsvp_event',
      actionDescription: `RSVP'd to event "${eventTitle}"`,
      targetType: 'event',
      targetId: eventId,
      targetName: eventTitle,
    }),

  editEvent: (user: User, eventTitle: string, eventId?: string) =>
    logActivity({
      user,
      actionType: 'edit_event',
      actionDescription: `Edited event "${eventTitle}"`,
      targetType: 'event',
      targetId: eventId,
      targetName: eventTitle,
    }),

  deleteEvent: (user: User, eventTitle: string, eventId?: string) =>
    logActivity({
      user,
      actionType: 'delete_event',
      actionDescription: `Deleted event "${eventTitle}"`,
      targetType: 'event',
      targetId: eventId,
      targetName: eventTitle,
    }),

  // Groups
  createGroup: (user: User, groupName: string, groupId?: string) =>
    logActivity({
      user,
      actionType: 'create_group',
      actionDescription: `Created new group "${groupName}"`,
      targetType: 'group',
      targetId: groupId,
      targetName: groupName,
    }),

  joinGroup: (user: User, groupName: string, groupId?: string) =>
    logActivity({
      user,
      actionType: 'join_group',
      actionDescription: `Joined group "${groupName}"`,
      targetType: 'group',
      targetId: groupId,
      targetName: groupName,
    }),

  editGroup: (user: User, groupName: string, groupId?: string) =>
    logActivity({
      user,
      actionType: 'edit_group',
      actionDescription: `Edited group "${groupName}"`,
      targetType: 'group',
      targetId: groupId,
      targetName: groupName,
    }),

  deleteGroup: (user: User, groupName: string, groupId?: string) =>
    logActivity({
      user,
      actionType: 'delete_group',
      actionDescription: `Deleted group "${groupName}"`,
      targetType: 'group',
      targetId: groupId,
      targetName: groupName,
    }),

  acceptMember: (user: User, memberName: string, groupName: string) =>
    logActivity({
      user,
      actionType: 'accept_member',
      actionDescription: `Accepted ${memberName} to group "${groupName}"`,
      targetType: 'group-member',
      targetName: `${memberName} - ${groupName}`,
    }),

  rejectMember: (user: User, memberName: string, groupName: string) =>
    logActivity({
      user,
      actionType: 'reject_member',
      actionDescription: `Rejected ${memberName} from group "${groupName}"`,
      targetType: 'group-member',
      targetName: `${memberName} - ${groupName}`,
    }),

  // Forum
  createForumTopic: (user: User, topicTitle: string, topicId?: string) =>
    logActivity({
      user,
      actionType: 'create_forum_topic',
      actionDescription: `Posted in Community Forum "${topicTitle}"`,
      targetType: 'forum-topic',
      targetId: topicId,
      targetName: topicTitle,
    }),

  replyForum: (user: User, topicTitle: string, topicId?: string) =>
    logActivity({
      user,
      actionType: 'reply_forum',
      actionDescription: `Replied to forum topic "${topicTitle}"`,
      targetType: 'forum-topic',
      targetId: topicId,
      targetName: topicTitle,
    }),

  // MWBI-608: likes are admin-activity-log only. Do not fan out to user-notification or email.
  likeForum: (user: User, topicTitle: string, topicId?: string) =>
    logActivity({
      user,
      actionType: 'like_forum',
      actionDescription: `Liked forum topic "${topicTitle}"`,
      targetType: 'forum-topic',
      targetId: topicId,
      targetName: topicTitle,
    }),

  // Blog
  createBlog: (user: User, blogTitle: string, blogId?: string) =>
    logActivity({
      user,
      actionType: 'create_blog',
      actionDescription: `Created new blog article "${blogTitle}"`,
      targetType: 'blog-post',
      targetId: blogId,
      targetName: blogTitle,
    }),

  publishBlog: (user: User, blogTitle: string, blogId?: string) =>
    logActivity({
      user,
      actionType: 'publish_blog',
      actionDescription: `Published blog article "${blogTitle}"`,
      targetType: 'blog-post',
      targetId: blogId,
      targetName: blogTitle,
    }),

  // MWBI-608: likes are admin-activity-log only. Do not fan out to user-notification or email.
  likeBlog: (user: User, blogTitle: string, blogId?: string) =>
    logActivity({
      user,
      actionType: 'like_blog',
      actionDescription: `Liked blog article "${blogTitle}"`,
      targetType: 'blog-post',
      targetId: blogId,
      targetName: blogTitle,
    }),

  commentBlog: (user: User, blogTitle: string, blogId?: string) =>
    logActivity({
      user,
      actionType: 'comment_blog',
      actionDescription: `Commented on blog article "${blogTitle}"`,
      targetType: 'blog-post',
      targetId: blogId,
      targetName: blogTitle,
    }),

  editBlog: (user: User, blogTitle: string, blogId?: string) =>
    logActivity({
      user,
      actionType: 'edit_blog',
      actionDescription: `Edited blog article "${blogTitle}"`,
      targetType: 'blog-post',
      targetId: blogId,
      targetName: blogTitle,
    }),

  deleteBlog: (user: User, blogTitle: string, blogId?: string) =>
    logActivity({
      user,
      actionType: 'delete_blog',
      actionDescription: `Deleted blog article "${blogTitle}"`,
      targetType: 'blog-post',
      targetId: blogId,
      targetName: blogTitle,
    }),

  // Volunteers
  createVolunteer: (user: User, opportunityTitle: string, opportunityId?: string, group?: GroupContext) =>
    logActivity({
      user,
      actionType: 'create_volunteer',
      actionDescription: `Created new volunteer opportunity "${clipLogText(opportunityTitle, MAX_LOG_TITLE_LENGTH)}"${inGroup(group)}`,
      targetType: 'volunteer-opportunity',
      targetId: opportunityId,
      targetName: opportunityTitle,
      metadata: groupMetadata(group),
    }),

  // Providers
  createProvider: (user: User, providerName: string, providerId?: string) =>
    logActivity({
      user,
      actionType: 'create_provider',
      actionDescription: `Created provider profile "${providerName}"`,
      targetType: 'therapy-provider',
      targetId: providerId,
      targetName: providerName,
    }),

  // Resources (Get Help)
  createResource: (user: User, resourceTitle: string, resourceId?: string) =>
    logActivity({
      user,
      actionType: 'create_resource',
      actionDescription: `Created new resource "${resourceTitle}"`,
      targetType: 'resource',
      targetId: resourceId,
      targetName: resourceTitle,
    }),

  editResource: (user: User, resourceTitle: string, resourceId?: string) =>
    logActivity({
      user,
      actionType: 'edit_resource',
      actionDescription: `Edited resource "${resourceTitle}"`,
      targetType: 'resource',
      targetId: resourceId,
      targetName: resourceTitle,
    }),

  // Team bios (About page)
  createTeamBio: (user: User, memberName: string, teamBioId?: string) =>
    logActivity({
      user,
      actionType: 'create_team_bio',
      actionDescription: `Created team bio for "${memberName}"`,
      targetType: 'team-bio',
      targetId: teamBioId,
      targetName: memberName,
    }),

  editTeamBio: (user: User, memberName: string, teamBioId?: string) =>
    logActivity({
      user,
      actionType: 'edit_team_bio',
      actionDescription: `Edited team bio for "${memberName}"`,
      targetType: 'team-bio',
      targetId: teamBioId,
      targetName: memberName,
    }),

  // Program cards (Our Work page)
  createProgramCard: (user: User, cardTitle: string, cardId?: string) =>
    logActivity({
      user,
      actionType: 'create_program_card',
      actionDescription: `Created program card "${cardTitle}"`,
      targetType: 'program-card',
      targetId: cardId,
      targetName: cardTitle,
    }),

  editProgramCard: (user: User, cardTitle: string, cardId?: string) =>
    logActivity({
      user,
      actionType: 'edit_program_card',
      actionDescription: `Edited program card "${cardTitle}"`,
      targetType: 'program-card',
      targetId: cardId,
      targetName: cardTitle,
    }),

  // Partners section (a single type, so it is only ever edited)
  editPartnersSection: (user: User, changedTiers: string[]) =>
    logActivity({
      user,
      actionType: 'edit_partners_section',
      actionDescription: changedTiers.length > 0
        ? `Edited the Partners section (${changedTiers.join(', ')})`
        : 'Edited the Partners section',
      targetType: 'partners-section',
      targetName: 'Partners section',
      metadata: { changedTiers },
    }),

  // User Management (Admin)
  archiveUser: (user: User, targetUserName: string) =>
    logActivity({
      user,
      actionType: 'archive_user',
      actionDescription: `Archived user ${targetUserName}`,
      targetType: 'user',
      targetName: targetUserName,
    }),

  unarchiveUser: (user: User, targetUserName: string) =>
    logActivity({
      user,
      actionType: 'unarchive_user',
      actionDescription: `Unarchived user ${targetUserName}`,
      targetType: 'user',
      targetName: targetUserName,
    }),

  changeRole: (user: User, targetUserName: string, newRole: string) =>
    logActivity({
      user,
      actionType: 'change_role',
      actionDescription: `Changed role for ${targetUserName} to ${newRole}`,
      targetType: 'user',
      targetName: targetUserName,
      metadata: { newRole },
    }),

  // Auth
  register: (user: User) =>
    logActivity({
      user,
      actionType: 'register',
      actionDescription: `Registered new account`,
    }),

  login: (user: User) =>
    logActivity({
      user,
      actionType: 'login',
      actionDescription: `Logged in`,
    }),

  // Content Reporting
  flagContent: (user: User, contentType: string, contentTitle: string, reason: string, contentId?: string) =>
    logActivity({
      user,
      actionType: 'flag_content',
      actionDescription: `Reported ${contentType}: "${contentTitle}" for ${reason}`,
      targetType: contentType,
      targetId: contentId,
      targetName: contentTitle,
      metadata: { reason },
    }),

  dismissFlag: (user: User, contentType: string, contentTitle: string, contentId?: string) =>
    logActivity({
      user,
      actionType: 'unflag_content',
      actionDescription: `Dismissed report for ${contentType}: "${contentTitle}"`,
      targetType: contentType,
      targetId: contentId,
      targetName: contentTitle,
    }),

  approveFlag: (user: User, contentType: string, contentTitle: string, contentId?: string) =>
    logActivity({
      user,
      actionType: 'delete_flagged',
      actionDescription: `Removed flagged ${contentType}: "${contentTitle}"`,
      targetType: contentType,
      targetId: contentId,
      targetName: contentTitle,
    }),
};

